// ============================================================================
// AdminPushTesting - admin panel to send test push notifications
// ============================================================================
// Find a user by username/UID, see their registered FCM device tokens, compose
// a title + body, and dispatch via /admin/sendTestPush. Preference checks
// (master toggle, quiet hours, per-type prefs) are intentionally bypassed by
// the server endpoint so admins can verify the FCM delivery pipeline
// independently of recipient settings. Use sparingly - this is for testing.
// ============================================================================
import React, { useState, useEffect } from 'react';
import {
  Box, Heading, Text, Input, HStack, VStack, Button, Alert, AlertIcon,
  FormControl, FormLabel, Badge, Spinner, Divider, Tag, Code, Avatar,
  Stat, StatLabel, StatNumber, StatGroup, Select,
} from '@chakra-ui/react';
import { Search, Send, Smartphone, RefreshCw, AlertTriangle, MailCheck } from 'lucide-react';
import gameApiService from '../../services/gameApiService';

interface SearchHit {
  id: string;
  username: string;
  photoURL?: string | null;
}

interface TokenInfo {
  token: string;
  platform: string;
  active: boolean;
  createdAt: number | null;
  updatedAt: number | null;
}

interface DeliveryReport {
  token: string;
  success: boolean;
  errorCode: string | null;
  messageId: string | null;
}

const platformBadge = (p: string) => {
  const color = p === 'ios' ? 'gray' : p === 'android' ? 'green' : 'purple';
  return <Badge colorScheme={color} fontSize="9px">{p.toUpperCase()}</Badge>;
};

const formatAge = (ts: number | null): string => {
  if (!ts) return '-';
  const ms = Date.now() - ts;
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const AdminPushTesting: React.FC = () => {
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [selected, setSelected] = useState<SearchHit | null>(null);
  const [tokens, setTokens] = useState<TokenInfo[]>([]);
  const [legacyToken, setLegacyToken] = useState<TokenInfo | null>(null);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(true);
  const [title, setTitle] = useState('Test Notification');
  const [body, setBody] = useState('This is a test push from the admin panel.');
  const [pushType, setPushType] = useState('admin_test');
  const [sending, setSending] = useState(false);
  const [report, setReport] = useState<{
    ok: boolean;
    summary: string;
    perToken?: DeliveryReport[];
  } | null>(null);
  // Browsable list of accounts that actually have a device registered.
  const [recipients, setRecipients] = useState<Array<{
    id: string; username: string | null; email: string | null;
    platform: string; pushEnabled: boolean;
    registeredAt: number | null; deviceCount: number;
  }>>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [packSending, setPackSending] = useState<string | null>(null);
  const [packReport, setPackReport] = useState<{ ok: boolean; msg: string } | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const sendPack = async (variant: string, testOnly: boolean) => {
    setPackSending(`${variant}:${testOnly ? 'test' : 'all'}`);
    setPackReport(null);
    const r = await gameApiService.packDropNotify({ variant, testOnly });
    setPackSending(null);
    if (!r.success) { setPackReport({ ok: false, msg: r.error || 'Send failed' }); return; }
    setPackReport({
      ok: true,
      msg: testOnly
        ? `Sent to you${r.reason ? ` (${r.reason})` : ''} - "${r.message?.title}"`
        : `Sent to ${r.sentTo ?? 0} of ${r.candidates ?? 0} devices${r.failed ? `, ${r.failed} failed` : ''}.`,
    });
  };

  const loadRecipients = async () => {
    setRecipientsLoading(true);
    const r = await gameApiService.pushRecipients(40);
    setRecipientsLoading(false);
    if (r.success && r.recipients) setRecipients(r.recipients);
  };

  // Show who can receive a push the moment the panel opens - no typing.
  useEffect(() => { loadRecipients(); }, []);

  const doSearch = async () => {
    if (!search.trim()) return;
    setSearching(true);
    setHits([]);
    // Allow direct paste of UID - skip search if it looks like one
    const looksLikeUid = /^[A-Za-z0-9]{20,}$/.test(search.trim());
    if (looksLikeUid) {
      setHits([{ id: search.trim(), username: '(direct UID)' }]);
      setSearching(false);
      return;
    }
    const r = await gameApiService.searchUsers(search.trim(), 10);
    setSearching(false);
    if (r.success && r.users) {
      setHits(r.users);
    } else {
      setHits([]);
    }
  };

  const verifyEmail = async () => {
    if (!selected) return;
    setVerifying(true);
    setVerifyResult(null);
    const r = await gameApiService.adminVerifyUser(selected.id);
    setVerifying(false);
    if (!r.success) {
      setVerifyResult({ ok: false, msg: r.error || 'Verify failed' });
      return;
    }
    setVerifyResult({
      ok: true,
      msg: r.alreadyVerified
        ? `${selected.username} was already verified`
        : `${selected.username} marked as email-verified (${r.email || 'no email'})`,
    });
  };

  const selectUser = async (u: SearchHit) => {
    setSelected(u);
    setReport(null);
    setVerifyResult(null);
    setTokens([]);
    setLegacyToken(null);
    setTokensLoading(true);
    const r = await gameApiService.getUserPushTokens(u.id);
    setTokensLoading(false);
    if (r.success) {
      setTokens(r.tokens || []);
      setLegacyToken(r.legacyToken as TokenInfo | null);
      setPushEnabled(r.pushNotificationsEnabled !== false);
    }
  };

  const refreshTokens = async () => {
    if (!selected) return;
    setTokensLoading(true);
    const r = await gameApiService.getUserPushTokens(selected.id);
    setTokensLoading(false);
    if (r.success) {
      setTokens(r.tokens || []);
      setLegacyToken(r.legacyToken as TokenInfo | null);
      setPushEnabled(r.pushNotificationsEnabled !== false);
    }
  };

  const send = async () => {
    if (!selected || !title.trim() || !body.trim()) return;
    setSending(true);
    setReport(null);
    const r = await gameApiService.sendTestPush(selected.id, title.trim(), body.trim(), { type: pushType });
    setSending(false);
    if (!r.success && r.reason === 'no_tokens') {
      setReport({
        ok: false,
        summary: r.message || 'No registered FCM tokens for this user.',
      });
      return;
    }
    if (r.error) {
      setReport({ ok: false, summary: r.error });
      return;
    }
    setReport({
      ok: (r.successCount || 0) > 0,
      summary: `Delivered ${r.successCount}/${r.tokensTried} - ${r.failureCount} failed${r.invalidatedCount ? `, ${r.invalidatedCount} invalidated` : ''}`,
      perToken: r.report,
    });
  };

  const totalActiveTokens = tokens.filter(t => t.active).length + (legacyToken ? 1 : 0);

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="md" color="cyan.300" mb={1}>Push Notification Test</Heading>
          <Text color="gray.400" fontSize="sm">
            Send a test push to any user's registered devices. Bypasses
            preference checks (quiet hours, type opt-outs) so you can verify
            FCM delivery in isolation. Logs to <Code mx={1} fontSize="xs">
            notificationLogs</Code> with <Code mx={1} fontSize="xs">
            adminTest: true</Code>.
          </Text>
        </Box>

        {/* ── Pack Drop broadcast ───────────────────────────────────────── */}
        <Box bg="gray.800" border="1px solid" borderColor="gray.700" borderRadius="md" p={5}>
          <VStack align="stretch" spacing={3}>
            <HStack>
              <Send size={18} color="#FFD700" />
              <Heading size="sm" color="yellow.300">Pack Drop broadcast</Heading>
            </HStack>
            <Text fontSize="xs" color="gray.400">
              Premade copy with a deep link that opens the drop, not the home screen.
              Send it to yourself first - the test goes only to your own devices.
            </Text>
            {packReport && (
              <Alert status={packReport.ok ? 'success' : 'error'}
                bg={packReport.ok ? 'green.900' : 'red.900'} borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">{packReport.msg}</Text>
              </Alert>
            )}
            {(['drop', 'reminder', 'lastcall'] as const).map(v => (
              <HStack key={v} spacing={2}>
                <Text fontSize="xs" color="gray.500" w="70px" textTransform="capitalize">{v}</Text>
                <Button size="xs" colorScheme="cyan" variant="outline" flex={1}
                  isLoading={packSending === v + ':test'}
                  onClick={() => sendPack(v, true)}>
                  Test on me
                </Button>
                <Button size="xs" colorScheme="yellow" flex={1}
                  isLoading={packSending === v + ':all'}
                  onClick={() => {
                    if (window.confirm(`Send the "${v}" Pack Drop push to EVERY registered device?`)) {
                      sendPack(v, false);
                    }
                  }}>
                  Send to all
                </Button>
              </HStack>
            ))}
          </VStack>
        </Box>

        {/* ── Registered devices: who can actually receive a push ───────── */}
        <Box bg="gray.800" border="1px solid" borderColor="gray.700" borderRadius="md" p={5}>
          <VStack align="stretch" spacing={3}>
            <HStack>
              <Smartphone size={18} color="#34d399" />
              <Heading size="sm" color="green.300">Registered devices</Heading>
              <Text fontSize="xs" color="gray.500">
                {recipientsLoading ? 'loading…' : `${recipients.length} account(s)`}
              </Text>
              <Button size="xs" ml="auto" variant="ghost" colorScheme="gray"
                leftIcon={<RefreshCw size={12} />}
                onClick={loadRecipients} isLoading={recipientsLoading}>
                Refresh
              </Button>
            </HStack>

            <Text fontSize="xs" color="gray.400">
              Everyone here has a device registered with FCM - tap one to target it.
              If this list is empty, nothing has ever registered a token and push
              cannot work yet, whatever you send.
            </Text>

            {!recipientsLoading && recipients.length === 0 && (
              <Alert status="warning" bg="orange.900" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  No registered devices. Open the app on a phone, accept the
                  notification prompt, then refresh.
                </Text>
              </Alert>
            )}

            <VStack align="stretch" spacing={1} maxH="260px" overflowY="auto">
              {recipients.map(r => (
                <HStack key={r.id}
                  px={3} py={2}
                  bg={selected?.id === r.id ? 'cyan.900' : 'gray.900'}
                  borderRadius="sm" cursor="pointer"
                  _hover={{ bg: 'gray.700' }}
                  onClick={() => selectUser({ id: r.id, username: r.username || r.email || r.id })}
                >
                  <Avatar size="xs" name={r.username || undefined} />
                  <VStack align="start" spacing={0} flex={1} minW={0}>
                    <Text fontSize="sm" color="white" noOfLines={1}>
                      {r.username || r.email || r.id}
                    </Text>
                    <HStack spacing={1}>
                      {platformBadge(r.platform)}
                      <Text fontSize="10px" color="gray.500" noOfLines={1}>
                      {r.deviceCount} device{r.deviceCount === 1 ? '' : 's'}
                      {r.registeredAt ? ` · ${formatAge(r.registeredAt)}` : ''}
                      </Text>
                    </HStack>
                  </VStack>
                  {!r.pushEnabled && (
                    <Text fontSize="10px" color="orange.300">prefs off</Text>
                  )}
                </HStack>
              ))}
            </VStack>
          </VStack>
        </Box>

        {/* ── User search ────────────────────────────────────────────────── */}
        <Box bg="gray.800" border="1px solid" borderColor="gray.700" borderRadius="md" p={5}>
          <VStack align="stretch" spacing={4}>
            <HStack>
              <Search size={18} color="#22d3ee" />
              <Heading size="sm" color="cyan.300">Target user</Heading>
            </HStack>
            <HStack>
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && doSearch()}
                placeholder="Username or paste UID directly"
                bg="gray.900" borderColor="gray.600" color="white"
              />
              <Button colorScheme="cyan" leftIcon={<Search size={14} />}
                onClick={doSearch} isLoading={searching}>Find</Button>
            </HStack>

            {hits.length > 0 && (
              <VStack align="stretch" spacing={1}>
                {hits.map(u => (
                  <HStack key={u.id}
                    px={3} py={2}
                    bg={selected?.id === u.id ? 'cyan.900' : 'gray.900'}
                    borderRadius="sm" cursor="pointer"
                    _hover={{ bg: 'gray.700' }}
                    onClick={() => selectUser(u)}
                  >
                    <Avatar size="xs" name={u.username} src={u.photoURL || undefined} />
                    <Text fontSize="sm" color="white" flex={1}>{u.username}</Text>
                    <Code fontSize="xs" bg="transparent" color="gray.500">{u.id.substring(0, 12)}…</Code>
                  </HStack>
                ))}
              </VStack>
            )}
          </VStack>
        </Box>

        {/* ── Selected user device list ──────────────────────────────────── */}
        {selected && (
          <Box bg="gray.800" border="1px solid" borderColor="gray.700" borderRadius="md" p={5}>
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between">
                <HStack>
                  <Smartphone size={18} color="#22d3ee" />
                  <Heading size="sm" color="cyan.300">Registered devices</Heading>
                </HStack>
                <HStack>
                  <Button size="xs" variant="outline" colorScheme="purple"
                    leftIcon={<MailCheck size={12} />}
                    onClick={verifyEmail} isLoading={verifying}
                    title="Mark this user's email as verified (test bypass)">
                    Verify email
                  </Button>
                  <Button size="xs" variant="ghost" color="gray.300"
                    leftIcon={<RefreshCw size={12} />}
                    onClick={refreshTokens} isLoading={tokensLoading}>Refresh</Button>
                </HStack>
              </HStack>

              {verifyResult && (
                <Alert status={verifyResult.ok ? 'success' : 'error'}
                  bg={verifyResult.ok ? 'green.900' : 'red.900'} color="white">
                  <AlertIcon />
                  <Box fontSize="sm">{verifyResult.msg}</Box>
                </Alert>
              )}

              <StatGroup>
                <Stat>
                  <StatLabel color="gray.400" fontSize="xs">Active tokens</StatLabel>
                  <StatNumber color={totalActiveTokens > 0 ? 'cyan.300' : 'red.300'}>
                    {totalActiveTokens}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel color="gray.400" fontSize="xs">Push toggle</StatLabel>
                  <StatNumber color={pushEnabled ? 'green.300' : 'red.300'} fontSize="md">
                    {pushEnabled ? 'ENABLED' : 'OFF'}
                  </StatNumber>
                </Stat>
              </StatGroup>

              {totalActiveTokens === 0 && !tokensLoading && (
                <Alert status="warning" bg="orange.900" color="white">
                  <AlertIcon />
                  <Box fontSize="sm">
                    No registered tokens. User must open the native iOS/Android app and
                    grant push permission. Web sessions don't register FCM tokens.
                  </Box>
                </Alert>
              )}

              {legacyToken && (
                <Box bg="gray.900" p={3} borderRadius="sm">
                  <HStack mb={1}>
                    <Tag size="sm" colorScheme="yellow">LEGACY</Tag>
                    {platformBadge(legacyToken.platform)}
                    <Text fontSize="xs" color="gray.500" ml="auto">
                      updated {formatAge(legacyToken.updatedAt)}
                    </Text>
                  </HStack>
                  <Code fontSize="xs" bg="transparent" color="gray.300" wordBreak="break-all">
                    {legacyToken.token.substring(0, 60)}…
                  </Code>
                </Box>
              )}

              {tokens.map(t => (
                <Box key={t.token} bg="gray.900" p={3} borderRadius="sm" opacity={t.active ? 1 : 0.5}>
                  <HStack mb={1}>
                    {platformBadge(t.platform)}
                    {!t.active && <Tag size="sm" colorScheme="red">INACTIVE</Tag>}
                    <Text fontSize="xs" color="gray.500" ml="auto">
                      updated {formatAge(t.updatedAt)}
                    </Text>
                  </HStack>
                  <Code fontSize="xs" bg="transparent" color="gray.300" wordBreak="break-all">
                    {t.token.substring(0, 60)}…
                  </Code>
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        {/* ── Compose + send ─────────────────────────────────────────────── */}
        {selected && (
          <Box bg="gray.800" border="1px solid" borderColor="cyan.800" borderRadius="md" p={5}>
            <VStack align="stretch" spacing={4}>
              <HStack>
                <Send size={18} color="#22d3ee" />
                <Heading size="sm" color="cyan.300">Compose</Heading>
              </HStack>

              <FormControl>
                <FormLabel color="gray.300" fontSize="xs">Title</FormLabel>
                <Input value={title} onChange={e => setTitle(e.target.value)}
                  maxLength={200} bg="gray.900" borderColor="gray.600" color="white" />
              </FormControl>

              <FormControl>
                <FormLabel color="gray.300" fontSize="xs">Body</FormLabel>
                <Input value={body} onChange={e => setBody(e.target.value)}
                  maxLength={2000} bg="gray.900" borderColor="gray.600" color="white" />
              </FormControl>

              <FormControl>
                <FormLabel color="gray.300" fontSize="xs">Notification type</FormLabel>
                <Select value={pushType} onChange={e => setPushType(e.target.value)}
                  bg="gray.900" borderColor="gray.600" color="white" maxW="280px"
                  sx={{ option: { background: '#171923', color: 'white' } }}>
                  <option value="admin_test">Admin test (generic)</option>
                  <option value="announcement">Announcement</option>
                  <option value="game_alert">Game alert</option>
                  <option value="table_invite">Table invite</option>
                  <option value="dm">Direct message</option>
                  <option value="mention">Mention</option>
                  <option value="promotion">Promotion</option>
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Sets the payload type so tapping the notification routes like the
                  real thing. Delivery still bypasses the user's preference toggles.
                </Text>
              </FormControl>

              <Button colorScheme="cyan" leftIcon={<Send size={16} />}
                onClick={send} isLoading={sending} loadingText="Sending"
                isDisabled={totalActiveTokens === 0 || !title.trim() || !body.trim()}
                alignSelf="flex-start">
                Send to {selected.username}
              </Button>

              {report && (
                <Alert status={report.ok ? 'success' : 'error'}
                  bg={report.ok ? 'green.900' : 'red.900'} color="white">
                  <AlertIcon />
                  <Box fontSize="sm">{report.summary}</Box>
                </Alert>
              )}

              {report?.perToken && report.perToken.length > 0 && (
                <Box bg="gray.900" p={3} borderRadius="sm">
                  <Text fontSize="xs" color="gray.400" mb={2}>Per-token result</Text>
                  <VStack align="stretch" spacing={1}>
                    {report.perToken.map((r, i) => (
                      <HStack key={i}>
                        <Badge colorScheme={r.success ? 'green' : 'red'} fontSize="9px">
                          {r.success ? 'OK' : 'FAIL'}
                        </Badge>
                        <Code fontSize="xs" bg="transparent" color="gray.400">{r.token}</Code>
                        {r.errorCode && (
                          <Text fontSize="xs" color="red.300">{r.errorCode}</Text>
                        )}
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          </Box>
        )}

        <Box bg="gray.900" border="1px solid" borderColor="gray.700" borderRadius="md" p={4}>
          <HStack align="start">
            <AlertTriangle size={16} color="#fbbf24" />
            <Text fontSize="xs" color="gray.400">
              <strong>Emulator note:</strong> FCM does not run in the Firebase
              Functions emulator. Test pushes from the emulator will fail with{' '}
              <Code fontSize="xs">messaging/internal</Code> or
              authentication errors. To verify delivery you must deploy the
              function to a real Firebase project (your staging project once
              set up).
            </Text>
          </HStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default AdminPushTesting;
